import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile as firebaseUpdateProfile,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithPopup,
  linkWithPhoneNumber,
  unlink,
  EmailAuthProvider,
  linkWithCredential,
  GoogleAuthProvider,
  PhoneAuthProvider,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import api from '../utils/api';
import toast from 'react-hot-toast';

/**
 * Auth state for the whole app.
 *
 * Identity (uid, providers, ID token, email-verified flag) lives in Firebase.
 * Profile data (role, addresses, dealer info, wishlist) lives in our MongoDB
 * via /api/auth/firebase-sync and /api/auth/me. We keep both in sync via the
 * `onAuthStateChanged` listener below: every time Firebase tells us the auth
 * state changed, we re-fetch the Mongo profile.
 */
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  // Firebase user (null when signed out)
  const [firebaseUser, setFirebaseUser] = useState(null);
  // MongoDB profile that mirrors the Firebase user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set when /firebase-sync replied 403 EMAIL_VERIFICATION_REQUIRED for the
  // current Firebase user. The user is signed in to Firebase (so they can
  // resend the verification email), but they have no Mongo profile yet, so
  // app routes treat them as unauthenticated. The "Check your email" view
  // on the auth page reads this state to render its UI.
  const [awaitingEmailVerification, setAwaitingEmailVerification] = useState(null);
  // We keep the latest pending registration data here so we can pass it to
  // /firebase-sync when onAuthStateChanged fires — Firebase's createUser*
  // promise resolves before the auth listener does, so we can't rely on
  // local-scope variables. Also persisted to localStorage with a 24h TTL so
  // role/profile data survives the verify-and-sign-in-again round-trip.
  const pendingProfileRef = useRef(null);

  // The currently-rendered reCAPTCHA widget for phone OTP. We clear it
  // between attempts (cancel + retry) to avoid Firebase's "reCAPTCHA has
  // already been rendered in this element" error.
  const recaptchaVerifierRef = useRef(null);

  /**
   * Destroy the existing reCAPTCHA widget (if any) and wipe the host div's
   * children so a new verifier can render cleanly. Safe to call when no
   * verifier exists.
   */
  const teardownRecaptcha = (containerId) => {
    try {
      if (recaptchaVerifierRef.current?.clear) {
        recaptchaVerifierRef.current.clear();
      }
    } catch (_) { /* widget may already be detached — ignore */ }
    recaptchaVerifierRef.current = null;
    if (containerId && typeof document !== 'undefined') {
      const el = document.getElementById(containerId);
      if (el) el.innerHTML = '';
    }
  };

  /**
   * Fetch (or create on first sign-in) the Mongo profile for the current
   * Firebase user. Returns the merged user object or null if signed out.
   *
   * If the backend responds with 403 EMAIL_VERIFICATION_REQUIRED (a
   * password sign-up that hasn't clicked the verification link yet), we
   * leave the user signed in to Firebase but set
   * `awaitingEmailVerification`. The Login page renders a "Check your
   * email" view in that state — Firebase's "resend verification email"
   * call needs `auth.currentUser`, so we deliberately don't sign out.
   */
  const syncProfile = useCallback(async (extraProfileData = null) => {
    try {
      const fbUser = auth.currentUser;
      if (!fbUser) {
        setUser(null);
        return null;
      }

      // Pull any pending profile data that survived a tab close — only
      // valid if the email matches and the entry hasn't expired (24h TTL).
      let pending = extraProfileData || pendingProfileRef.current;
      if (!pending) {
        try {
          const raw = localStorage.getItem('ce_pending_profile');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (
              parsed?.email === fbUser.email?.toLowerCase() &&
              typeof parsed?.expiresAt === 'number' &&
              parsed.expiresAt > Date.now()
            ) {
              pending = parsed;
            }
            // One-shot: always clear after attempting to read.
            localStorage.removeItem('ce_pending_profile');
          }
        } catch (_) { /* non-fatal */ }
      }

      // Railway cold-starts can take 15-30s — use a generous timeout and retry once
      let syncRes;
      try {
        syncRes = await api.post('/auth/firebase-sync', pending || {}, { timeout: 30000 });
      } catch (retryErr) {
        if (retryErr.code === 'ECONNABORTED' || retryErr.message?.includes('timeout')) {
          // Retry once on timeout (cold start likely finished by now)
          syncRes = await api.post('/auth/firebase-sync', pending || {}, { timeout: 30000 });
        } else {
          throw retryErr;
        }
      }
      pendingProfileRef.current = null;
      setAwaitingEmailVerification(null);
      setUser(syncRes.data.data.user);

      // Merge guest cart on first authenticated request after sign-in.
      const sessionId = localStorage.getItem('ce_session');
      if (sessionId) {
        try {
          await api.post('/cart/merge', { sessionId });
          localStorage.removeItem('ce_session');
        } catch (_) { /* non-fatal */ }
      }

      return syncRes.data.data.user;
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.code === 'EMAIL_VERIFICATION_REQUIRED') {
        // Hold the user in a verification-pending state. The "Check your
        // email" view on the auth page picks this up via context. We do
        // NOT sign out — keeping `auth.currentUser` populated lets the
        // user click "Resend verification email" without re-typing their
        // password.
        setAwaitingEmailVerification({
          email: auth.currentUser?.email || null,
        });
        setUser(null);
        return null;
      }
      // Anything else: drop to signed-out state.
      console.error('[auth] profile sync failed:', err);
      setUser(null);
      return null;
    }
  }, []);

  // Subscribe to Firebase auth state. This fires:
  //   • once on mount (whatever persisted state IndexedDB had)
  //   • on every sign-in / sign-out / token refresh
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        await syncProfile();
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [syncProfile]);

  // ---------------------------------------------------------------------------
  // Sign-in flows
  // ---------------------------------------------------------------------------

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    // onAuthStateChanged will fire and call syncProfile; nothing to do here.
    return cred.user;
  };

  /**
   * Register a new account with email + password.
   *
   *   1. Creates the Firebase user (Firebase auto-signs them in).
   *   2. Sets the displayName.
   *   3. Sends the verification email and waits for it to be queued so we
   *      can surface a real error if delivery fails.
   *   4. Persists the role/phone/consent the user typed to localStorage
   *      (24h TTL, keyed by email) so the data survives the verify-and-
   *      come-back round-trip. syncProfile reads this back on next sign-in.
   *
   * The auth state right after this call is "signed in to Firebase, no
   * Mongo profile yet." The first /firebase-sync attempt will fail with
   * 403 EMAIL_VERIFICATION_REQUIRED, which AuthContext catches and turns
   * into the `awaitingEmailVerification` state — the Login page renders a
   * "Check your email" screen at that point.
   */
  const register = async ({ name, email, password, phone, role, profile, consent }) => {
    const profileSnapshot = { name, phone, role, profile, consent };
    pendingProfileRef.current = profileSnapshot;
    try {
      localStorage.setItem('ce_pending_profile', JSON.stringify({
        ...profileSnapshot,
        email: email.toLowerCase(),
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      }));
    } catch (_) { /* localStorage unavailable — fall back to in-memory ref */ }

    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      try { await firebaseUpdateProfile(cred.user, { displayName: name }); } catch (_) { /* non-fatal */ }
    }
    // Block on the verification email so we can show a real error if it
    // fails. Without this we'd silently leave the user staring at a "Check
    // your inbox" screen with no email coming.
    await sendEmailVerification(cred.user);
    return cred.user;
  };

  const loginWithGoogle = async () => {
    // OAuth providers always come back with email_verified=true, so the
    // backend will happily link an existing email-only account.
    const cred = await signInWithPopup(auth, googleProvider);
    return cred.user;
  };

  /**
   * Phone OTP — two-step. Caller invokes startPhoneSignIn() to get a
   * `confirmationResult`, then calls confirmPhoneCode(code) on it.
   *
   * `recaptchaContainerId` must be the id of an empty <div> on the page —
   * Firebase mounts an invisible reCAPTCHA widget into it.
   *
   * We track the live verifier in a ref and tear it down before creating a
   * new one, otherwise Firebase throws "reCAPTCHA has already been
   * rendered in this element" on the second attempt within the same page
   * load (e.g. after the user clicks Cancel and tries another number).
   */
  const startPhoneSignIn = async (phoneE164, recaptchaContainerId) => {
    teardownRecaptcha(recaptchaContainerId);
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = verifier;
    await verifier.render();
    const confirmationResult = await signInWithPhoneNumber(auth, phoneE164, verifier);
    return confirmationResult;
  };

  const confirmPhoneCode = async (confirmationResult, code) => {
    const cred = await confirmationResult.confirm(code);
    // Successful verification — clear the reCAPTCHA so the next attempt
    // (e.g. account linking later) starts fresh.
    teardownRecaptcha();
    return cred.user;
  };

  // ---------------------------------------------------------------------------
  // Account linking — adds extra sign-in methods to the *current* user
  // ---------------------------------------------------------------------------

  const linkGoogle = async () => {
    if (!auth.currentUser) throw new Error('Not signed in');
    const cred = await linkWithPopup(auth.currentUser, googleProvider);
    return cred.user;
  };

  const startLinkPhone = async (phoneE164, recaptchaContainerId) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    teardownRecaptcha(recaptchaContainerId);
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = verifier;
    await verifier.render();
    const confirmationResult = await linkWithPhoneNumber(auth.currentUser, phoneE164, verifier);
    return confirmationResult;
  };

  const confirmLinkPhoneCode = async (confirmationResult, code) => {
    const result = await confirmationResult.confirm(code);
    teardownRecaptcha();
    return result;
  };

  /**
   * Add an email/password credential to a phone-only or Google-only account.
   * After this, the user can also sign in with email + password.
   */
  const linkEmailPassword = async (email, password) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    const credential = EmailAuthProvider.credential(email, password);
    const result = await linkWithCredential(auth.currentUser, credential);
    return result.user;
  };

  const unlinkProvider = async (providerId) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    return unlink(auth.currentUser, providerId);
  };

  // ---------------------------------------------------------------------------
  // Other operations
  // ---------------------------------------------------------------------------

  const logout = async () => {
    // Don't carry pending sign-up data from one user to the next.
    try { localStorage.removeItem('ce_pending_profile'); } catch (_) { /* non-fatal */ }
    await firebaseSignOut(auth);
    setUser(null);
    setFirebaseUser(null);
    setAwaitingEmailVerification(null);
  };

  const sendPasswordReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) throw new Error('Not signed in');
    await sendEmailVerification(auth.currentUser);
  };

  /**
   * After the user clicks the verification link in their email, force-refresh
   * the ID token (so it carries email_verified=true) and retry profile sync.
   * Used by the "I've verified, continue" button on the Check-your-email view.
   */
  const retryAfterVerification = async () => {
    if (!auth.currentUser) throw new Error('Not signed in');
    // Force-refresh the ID token so its email_verified claim reflects the
    // server-side verification that just happened. Without `true` the cached
    // token still says email_verified=false for up to an hour.
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true);
    return syncProfile();
  };

  const cancelEmailVerification = async () => {
    // User wants out of the verification-pending state — sign out cleanly.
    setAwaitingEmailVerification(null);
    try { await firebaseSignOut(auth); } catch (_) { /* non-fatal */ }
  };

  /**
   * Update editable profile fields on the Mongo doc. Identity fields (email,
   * password, phone) are managed via the Firebase SDK — see linkEmailPassword,
   * linkPhone, etc.
   */
  const updateProfile = async (data) => {
    const res = await api.put('/auth/profile', data);
    setUser(res.data.data);
    return res.data.data;
  };

  // Available linked providers for the current user — handy for the Dashboard.
  const linkedProviders = firebaseUser?.providerData?.map(p => p.providerId) || [];

  const value = {
    // identity
    firebaseUser,
    user,
    loading,
    awaitingEmailVerification,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'staff',
    isDealer: user?.role === 'dealer',
    isEmailVerified: !!firebaseUser?.emailVerified,
    linkedProviders,

    // sign-in
    login,
    register,
    loginWithGoogle,
    startPhoneSignIn,
    confirmPhoneCode,

    // linking
    linkGoogle,
    startLinkPhone,
    confirmLinkPhoneCode,
    linkEmailPassword,
    unlinkProvider,

    // other
    logout,
    sendPasswordReset,
    resendVerificationEmail,
    retryAfterVerification,
    cancelEmailVerification,
    updateProfile,
    refreshProfile: syncProfile,

    // expose provider-id constants so consumers don't have to import firebase/auth directly
    PROVIDER_GOOGLE: GoogleAuthProvider.PROVIDER_ID,
    PROVIDER_PHONE: PhoneAuthProvider.PROVIDER_ID,
    PROVIDER_PASSWORD: 'password',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
